import React, { useState } from "react";
import { Sparkles, X, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { DESTINATIONS } from "../../data/destinations";
import ItineraryResult from "./ItineraryResult";
import PlannerFallback from "./PlannerFallback";

const API = process.env.REACT_APP_BACKEND_URL;

const countryOf = (c) => (c.includes(",") ? c.split(",").pop() : c).trim();
const COUNTRIES = [...new Set(DESTINATIONS.map((d) => countryOf(d.country)))].sort();
const CATALOG = DESTINATIONS.map((d) => ({ id: d.id, title: d.title, country: d.country, category: d.category }));

const DAY_OPTIONS = [5, 7, 10, 14];
const TRIP_TYPES = [
  { key: "yoga", label: "Yoga Retreats" },
  { key: "adventure", label: "Adventure" },
  { key: "wellness", label: "Wellness & Party" },
  { key: "travel", label: "Sightseeing & Travel" },
  { key: "mix", label: "A mix of everything" },
];

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-3.5 py-1.5 text-sm transition ${
      active ? "border-brand-ink bg-brand-ink text-white" : "border-brand-line text-brand-ink hover:border-brand-ink"
    }`}
  >
    {children}
  </button>
);

const STEPS = ["country", "days", "types", "dates"];

export const AiConcierge = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("country");
  const [country, setCountry] = useState("");
  const [days, setDays] = useState(7);
  const [types, setTypes] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setStep("country"); setCountry(""); setDays(7); setTypes([]);
    setFrom(""); setTo(""); setResult(null); setError(""); setLoading(false);
  };
  const close = () => { setOpen(false); };

  const toggleType = (key) => {
    if (key === "mix") { setTypes(["mix"]); return; }
    setTypes((prev) => {
      const next = prev.filter((t) => t !== "mix");
      return next.includes(key) ? next.filter((t) => t !== key) : [...next, key];
    });
  };

  const goNext = () => {
    const i = STEPS.indexOf(step);
    if (i < STEPS.length - 1) setStep(STEPS[i + 1]);
  };
  const goBack = () => {
    const i = STEPS.indexOf(step);
    if (i > 0) setStep(STEPS[i - 1]);
  };

  const submit = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await fetch(`${API}/api/plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start_country: country.trim() || "anywhere in Europe",
          days,
          trip_types: types.length ? types : ["mix"],
          date_from: from || null,
          date_to: to || null,
          catalog: CATALOG,
        }),
      });
      if (!r.ok) throw new Error("bad");
      const data = await r.json();
      setResult(data);
    } catch (_) {
      setError("Our planner is taking a quick break. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-concierge-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-3.5 text-sm font-medium text-white shadow-xl shadow-black/20 transition hover:bg-brand-inkSoft"
      >
        <Sparkles size={16} className="text-brand-terracotta" /> Plan my Europe trip
      </button>

      {open && (
        <div data-testid="ai-concierge-dialog" className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-4 md:items-center">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-7 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-brand-inkSoft">AI Trip Planner</p>
                <h3 className="font-display mt-2 text-2xl leading-tight">Let&apos;s build your European escape.</h3>
              </div>
              <button data-testid="ai-concierge-close" onClick={close} className="rounded-full p-2 text-brand-inkSoft hover:bg-brand-bg2">
                <X size={16} />
              </button>
            </div>

            {/* RESULT */}
            {result ? (
              <>
                {result.stops.length > 0 ? (
                  <ItineraryResult result={result} requestedDays={days} onNavigate={close} />
                ) : (
                  <PlannerFallback country={country.trim()} from={from} to={to} notes={result.notes} />
                )}
                <button
                  data-testid="ai-plan-again"
                  onClick={reset}
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-line px-5 py-2.5 text-sm font-medium text-brand-ink hover:border-brand-ink"
                >
                  <ArrowLeft size={14} /> Plan another trip
                </button>
              </>
            ) : loading ? (
              <div data-testid="ai-loading" className="flex flex-col items-center justify-center gap-3 py-16 text-brand-inkSoft">
                <Loader2 size={28} className="animate-spin text-brand-terracotta" />
                <p className="text-sm">Mapping your route across Europe…</p>
              </div>
            ) : (
              <div className="mt-6">
                {/* STEP: COUNTRY */}
                {step === "country" && (
                  <div data-testid="step-country">
                    <p className="text-base font-medium text-brand-ink">Which country would you like to start from?</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {COUNTRIES.map((c) => (
                        <Chip key={c} testid={`ai-country-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} active={country === c} onClick={() => setCountry(c)}>
                          {c}
                        </Chip>
                      ))}
                    </div>
                    <input
                      data-testid="ai-country-input"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder="…or type a city / country"
                      className="mt-4 w-full rounded-xl border border-brand-line bg-brand-bg p-3 text-sm text-brand-ink focus:border-brand-ink focus:outline-none"
                    />
                  </div>
                )}

                {/* STEP: DAYS */}
                {step === "days" && (
                  <div data-testid="step-days">
                    <p className="text-base font-medium text-brand-ink">How many days are you planning?</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {DAY_OPTIONS.map((d) => (
                        <Chip key={d} testid={`ai-days-${d}`} active={days === d} onClick={() => setDays(d)}>
                          {d} days
                        </Chip>
                      ))}
                    </div>
                    <input
                      data-testid="ai-days-input"
                      type="number"
                      min={2}
                      max={60}
                      value={days}
                      onChange={(e) => setDays(Math.max(2, Math.min(60, Number(e.target.value) || 2)))}
                      className="mt-4 w-28 rounded-xl border border-brand-line bg-brand-bg p-3 text-sm text-brand-ink focus:border-brand-ink focus:outline-none"
                    />
                  </div>
                )}

                {/* STEP: TYPES */}
                {step === "types" && (
                  <div data-testid="step-types">
                    <p className="text-base font-medium text-brand-ink">What kind of trip do you want? (pick one or more)</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {TRIP_TYPES.map((t) => (
                        <Chip key={t.key} testid={`ai-type-${t.key}`} active={types.includes(t.key)} onClick={() => toggleType(t.key)}>
                          {t.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP: DATES */}
                {step === "dates" && (
                  <div data-testid="step-dates">
                    <p className="text-base font-medium text-brand-ink">When are you travelling? <span className="font-normal text-brand-inkSoft">(optional)</span></p>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="overline text-brand-inkSoft">From</span>
                        <input data-testid="ai-date-from" type="date" value={from} min={today} onChange={(e) => setFrom(e.target.value)} className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm text-brand-ink" />
                      </label>
                      <label className="block">
                        <span className="overline text-brand-inkSoft">To</span>
                        <input data-testid="ai-date-to" type="date" value={to} min={from || today} onChange={(e) => setTo(e.target.value)} className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm text-brand-ink" />
                      </label>
                    </div>
                  </div>
                )}

                {error && <p data-testid="ai-concierge-error" className="mt-4 text-sm text-red-600">{error}</p>}

                {/* NAV */}
                <div className="mt-7 flex items-center justify-between">
                  <button
                    data-testid="ai-step-back"
                    onClick={goBack}
                    disabled={step === "country"}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-inkSoft hover:text-brand-ink disabled:opacity-0"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>

                  {step !== "dates" ? (
                    <button
                      data-testid="ai-step-next"
                      onClick={goNext}
                      disabled={(step === "country" && !country.trim()) || (step === "types" && types.length === 0)}
                      className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-medium text-white disabled:opacity-50"
                    >
                      Next <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button
                      data-testid="ai-plan-submit"
                      onClick={submit}
                      className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-medium text-white"
                    >
                      <Sparkles size={14} className="text-brand-terracotta" /> Plan my trip
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AiConcierge;
